/**
 * Shared query-invalidation helpers for the post-turn refresh.
 *
 * The "profile cluster" (skills / leak-report / profile-summary /
 * recommended-drill / skills-history-overall) goes stale whenever signals
 * change — a graded drill turn or a profile reset. Centralized here so the
 * key list lives in ONE place (it was inlined in DrillView and ResetSkillProfile).
 */

import type { QueryClient } from "@tanstack/react-query";

/** Every derive-on-read profile surface that depends on skill_signals. */
export const PROFILE_QUERY_KEYS = [
  "skills",
  "leak-report",
  "profile-summary",
  "recommended-drill",
  "skills-history-overall",
] as const;

export function invalidateProfileCluster(queryClient: QueryClient): void {
  for (const key of PROFILE_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/**
 * Invalidate the surfaces a completed `/chat` (or `/drill`) turn affects.
 * Always refreshes billing-status (the usage chip + Settings). Daily turns
 * also refresh the daily-challenge state. Drill turns (which always add
 * signals) pass `includeProfile` to refresh the profile cluster too —
 * ChatView omits it because it also serves non-drill modes.
 */
export function invalidateAfterTurn(
  queryClient: QueryClient,
  opts: { isDaily: boolean; includeProfile?: boolean },
): void {
  queryClient.invalidateQueries({ queryKey: ["billing-status"] });
  if (opts.isDaily) {
    queryClient.invalidateQueries({ queryKey: ["daily-challenge"] });
  }
  if (opts.includeProfile) {
    invalidateProfileCluster(queryClient);
  }
}
